/**
 * Talker-facing Stripe / Connect errors. Never dump capability or Accounts v1 text.
 * Keep this file free of other util imports so the Node server can require it.
 */

const TALK_SESSION_LISTING_TYPE = 'talksession';

export const STRIPE_REQUESTED_CAPABILITIES = ['card_payments', 'transfers'];

export const LISTENER_PAYOUTS_NOT_READY_MESSAGE =
  'This listener isn’t set up to take bookings yet.';

export const STRIPE_ACCOUNTS_V1_SUPPORT_MESSAGE =
  'Payouts for this listener aren’t ready yet. Your card was not charged.';

const DESTINATION_CAPABILITY_RE = /destination account needs to have at least one of the following capabilities|transfers,\s*crypto_transfers,\s*legacy_payments/i;

const ACCOUNTS_V1_RE = /accounts v1 support|no longer recommends Accounts v1|feat_accounts_v1_support|POST \/v2\/core\/accounts/i;

/**
 * Flatten likely Stripe / Sharetribe error text from a string or storable error.
 *
 * @param {string|Object|null|undefined} errorOrText
 * @returns {string}
 */
export const stripeErrorText = errorOrText => {
  if (!errorOrText) {
    return '';
  }
  if (typeof errorOrText === 'string') {
    return errorOrText;
  }
  const apiErrors = errorOrText.apiErrors || errorOrText.data?.errors || [];
  const fromApi = apiErrors
    .map(apiError => apiError?.meta?.stripeMessage || apiError?.detail || apiError?.title)
    .filter(Boolean)
    .join(' ');
  return [fromApi, errorOrText.statusText, errorOrText.message, errorOrText.data?.message]
    .filter(Boolean)
    .join(' ');
};

/**
 * Destination charges require transfers (or legacy_payments) on the listener account.
 *
 * @param {string|Object|null|undefined} errorOrText
 * @returns {boolean}
 */
export const isStripeDestinationCapabilityError = errorOrText =>
  DESTINATION_CAPABILITY_RE.test(stripeErrorText(errorOrText));

/**
 * Sharetribe Flex still creates Connect Custom accounts with Accounts v1.
 *
 * @param {string|Object|null|undefined} errorOrText
 * @returns {boolean}
 */
export const isStripeAccountsV1SupportError = errorOrText =>
  ACCOUNTS_V1_RE.test(stripeErrorText(errorOrText));

/**
 * Replace raw Stripe capability / Accounts v1 text with marketplace copy.
 * Card declines and other payment messages pass through.
 *
 * @param {string|null|undefined} message
 * @returns {string|null|undefined}
 */
export const sanitizeStripeUserMessage = message => {
  if (!message || typeof message !== 'string') {
    return message;
  }
  if (isStripeDestinationCapabilityError(message) || isStripeAccountsV1SupportError(message)) {
    return LISTENER_PAYOUTS_NOT_READY_MESSAGE;
  }
  return message;
};

export const LISTING_PAYOUTS_READY = 'ready';
export const LISTING_PAYOUTS_NOT_READY = 'notReady';
export const LISTING_PAYOUTS_UNKNOWN = 'unknown';

/**
 * Search / home cards often omit publicData.payoutsReady. Missing is unknown,
 * not “not set up” — a 429 or skipped listings.show must not close Book a time.
 *
 * @param {Object} [listing]
 * @returns {'ready'|'notReady'|'unknown'}
 */
export const listingPayoutsStatus = listing => {
  if (!listing) {
    return LISTING_PAYOUTS_UNKNOWN;
  }
  const payoutsReady = listing?.attributes?.publicData?.payoutsReady;
  if (payoutsReady === true) {
    return LISTING_PAYOUTS_READY;
  }
  if (payoutsReady === false) {
    return LISTING_PAYOUTS_NOT_READY;
  }
  return listing?.attributes?.publicData?.listingType === TALK_SESSION_LISTING_TYPE
    ? LISTING_PAYOUTS_UNKNOWN
    : LISTING_PAYOUTS_READY;
};

/**
 * True when a cached listing is safe to reuse without listings.show.
 *
 * @param {Object} [listing]
 * @returns {boolean}
 */
export const listingHasKnownPayoutsReady = listing =>
  listingPayoutsStatus(listing) !== LISTING_PAYOUTS_UNKNOWN;

/**
 * Paid Book / Start now stay off only when payoutsReady is explicitly false.
 * Search cards omit the field — treat that as unknown and keep the panel up.
 *
 * @param {Object} [listing]
 * @returns {boolean}
 */
export const listingAcceptsPaidBookings = listing => {
  if (!listing) {
    return true;
  }
  // Do not use author.stripeConnected. Public author payloads often omit it
  // or send false, which made bookable profiles look closed to guests.
  return listingPayoutsStatus(listing) !== LISTING_PAYOUTS_NOT_READY;
};

/**
 * True when checkout must stop before card collection / Confirm booking.
 *
 * @param {Object} [params]
 * @param {Object} [params.listing]
 * @param {Object} [params.initiateOrderError]
 * @param {Object} [params.confirmPaymentError]
 * @param {Object} [params.confirmCardPaymentError]
 * @param {Object} [params.speculateTransactionError]
 * @returns {boolean}
 */
export const shouldBlockPaidCheckout = ({
  listing,
  initiateOrderError,
  confirmPaymentError,
  confirmCardPaymentError,
  speculateTransactionError,
} = {}) => {
  if (listing && !listingAcceptsPaidBookings(listing)) {
    return true;
  }
  return [
    initiateOrderError,
    confirmPaymentError,
    confirmCardPaymentError,
    speculateTransactionError,
  ].some(
    error => isStripeDestinationCapabilityError(error) || isStripeAccountsV1SupportError(error)
  );
};
